import { format, formatDistanceToNowStrict, isAfter, isSameYear, parseISO, startOfToday } from 'date-fns';
import { ru } from 'date-fns/locale';

/** Форматирование дат и чисел. Все даты приходят ISO-8601 (docs/05 §1). */

export function formatDate(value: string | null | undefined): string {
  if (!value) {
    return '—';
  }
  const parsed = parseISO(value);
  return isSameYear(parsed, new Date()) ? format(parsed, 'd MMM', { locale: ru }) : format(parsed, 'd MMM yyyy', { locale: ru });
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) {
    return '—';
  }
  return format(parseISO(value), 'd MMM yyyy, HH:mm', { locale: ru });
}

export function formatDateRange(from: string | null, to: string | null): string {
  if (!from && !to) {
    return 'Даты не заданы';
  }
  return `${formatDate(from)} — ${formatDate(to)}`;
}

/** «5 минут назад» — для истории и комментариев. */
export function formatRelative(value: string | null | undefined): string {
  if (!value) {
    return '—';
  }
  return formatDistanceToNowStrict(parseISO(value), { locale: ru, addSuffix: true });
}

export function isOverdue(value: string | null | undefined): boolean {
  if (!value) {
    return false;
  }
  return isAfter(startOfToday(), parseISO(value));
}

/** Группировка истории: «Сегодня / Вчера / дата» (docs/06 §3.2). */
export function groupDateLabel(value: string): string {
  const parsed = parseISO(value);
  const today = startOfToday();
  const dayMs = 24 * 60 * 60 * 1000;
  const diff = today.getTime() - new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()).getTime();
  if (diff <= 0) {
    return 'Сегодня';
  }
  if (diff === dayMs) {
    return 'Вчера';
  }
  return format(parsed, 'd MMMM yyyy', { locale: ru });
}

export function formatPoints(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '—';
  }
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

/** Размер файла для списка вложений. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} Б`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(0)} КБ`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}

export function formatCount(value: number, one: string, few: string, many: string): string {
  const mod10 = value % 10;
  const mod100 = value % 100;
  if (mod10 === 1 && mod100 !== 11) {
    return `${value} ${one}`;
  }
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
    return `${value} ${few}`;
  }
  return `${value} ${many}`;
}

/** `YYYY-MM-DD` для input[type=date]. */
export function toDateInputValue(value: string | null | undefined): string {
  return value ?? '';
}

export function todayIso(): string {
  return format(new Date(), 'yyyy-MM-dd');
}
